import { defaultOnOf } from "../settings/catalog";
import { SCREENS, stepShowKey } from "../routes/registry";

/**
 * LE PARCOURS D'UN ACCOMPAGNANT — décorrélé de celui du porteur.
 *
 * ⚠️ UN SECOND FIL, ET NON UN FILTRE SUR LE PREMIER. C'est la demande, et le produit lui
 * donne raison : la V1 sert à un accompagnant (`participation === 5`) un ordre
 * DIFFÉRENT — informations d'abord, puis les dates et la billetterie, là où le porteur
 * voit ses dates avant son formulaire (`screens/steps/Steps.jsx`). Un sous-ensemble du
 * fil principal aurait forcé le même ordre et les mêmes interrupteurs.
 *
 * ⚠️ CE QUI N'Y FIGURE PAS, ET POURQUOI :
 *
 *   participation  Un accompagnant ne décline pas : on l'inscrit. La V1 force
 *                  `isRemote: false` sans jamais poser la question.
 *   typologie      HÉRITÉE DU PORTEUR — la V1 recopie ses `typo` et `typoId` à la
 *                  création (`screens/steps/Informations.jsx`). La demander reviendrait
 *                  à laisser quelqu'un inscrire un exposant sous son propre nom.
 *   société        Elle rattache une PERSONNE à son employeur ; celui qui accompagne
 *                  vient avec le porteur, pas au titre d'une société qu'il déclarerait.
 *                  EXCEPT a person to attach to a company (coworkers offered, or the
 *                  organiser asks for it) when the bearer has
 *                  several companies: a step of its own, FIRST, asks which of the
 *                  BEARER'S companies they come under. See `COMPANY_STEP`.
 *   confirmation   Il n'y en a pas : c'est le porteur qui confirme, une fois, pour tout
 *                  le monde. Un accompagnant n'a rien à valider de son côté.
 */

/** Une étape du sous-parcours. */
export type CompanionStep = {
  /** Identifiant du réglage et de l'étape — jamais affiché. */
  key: string;
  /** Ce que le fil affiche, en français : le repli de `nameKey`. */
  name: string;
  /**
   * La clé du dictionnaire qui le traduit : celle du défaut de l'étape du porteur, qui
   * porte le même mot. Lue par `t()`, jamais par `useText` : le nom que l'organisateur
   * donne à l'étape du porteur ne renomme pas le fil de l'accompagnant.
   */
  nameKey: string;
  /**
   * Peut-elle être retirée ?
   *
   * ⚠️ LES INFORMATIONS NE LE PEUVENT PAS : c'est le formulaire qui CRÉE la personne.
   * Sans lui il n'y a ni nom, ni compte, ni badge — le reste du parcours n'aurait
   * personne à qui s'appliquer.
   */
  removable?: boolean;
};

/**
 * LES ÉTAPES POSSIBLES, DANS L'ORDRE DE LA V1.
 *
 * ⚠️ LES INFORMATIONS D'ABORD, ET CE N'EST PAS INDIFFÉRENT : les trois suivantes ont
 * besoin d'un identifiant pour écrire quoi que ce soit, et cet identifiant n'existe
 * qu'une fois le formulaire soumis. C'est l'ordre que la V1 impose pour la même raison.
 */
export const COMPANION_FUNNEL: ReadonlyArray<CompanionStep> = [
  {
    key: "informations",
    name: "Informations",
    nameKey: "FO_MEETING_V2_INFO_STEP_LABEL",
  },
  {
    key: "dates",
    name: "Dates",
    nameKey: "FO_MEETING_V2_DATES_STEP_LABEL",
    removable: true,
  },
  {
    key: "articles",
    name: "Billetterie",
    nameKey: "FO_MEETING_V2_ARTICLES_STEP_LABEL",
    removable: true,
  },
  {
    key: "conferences",
    name: "Conférences",
    nameKey: "FO_MEETING_V2_CONFERENCES_STEP_LABEL",
    removable: true,
  },
];

/**
 * THE COMPANY STEP — a coworker's, and only when there is a choice to make.
 *
 * ⚠️ NOT A STEP THE ORGANISER SWITCHES, hence outside `COMPANION_FUNNEL`: it comes with
 * « Proposer ses collaborateurs » or « Rattacher les accompagnants à une société », and
 * only when the bearer has SEVERAL companies. With
 * a single one, the person comes under it with no question (`funnel/companionCompany.ts`).
 *
 * ⚠️ FIRST, BEFORE THE FORM THAT CREATES THE PERSON. Nothing is written there for a new
 * person: the choice waits for the form, which creates them and attaches them in the
 * same « Continuer ». « Annuler » at either step therefore never leaves anybody created
 * under no company, which a step placed after the form would. For someone who already
 * exists (« Modifier », « Choisir »), leaving the step writes their company.
 */
export const COMPANY_STEP: CompanionStep = {
  key: "company",
  name: "Société",
  nameKey: "FO_MEETING_V2_COMPANY_STEP_LABEL",
};

/** L'interrupteur qui garde une étape dans le sous-parcours. */
export function companionStepKey(step: CompanionStep): string {
  return `FO_MEETING_V2_COMPANION_STEP_${step.key.toUpperCase()}_SHOW`;
}

type Read = (key: string) => unknown;

function on(read: Read, key: string): boolean {
  const raw = read(key);
  return typeof raw === "boolean" ? raw : defaultOnOf(key);
}

/**
 * ═══ LES ÉTAPES QUI N'EXISTENT QUE SI LE MODULE EXISTE ═══
 *
 * ⚠️ « DÉCORRÉLÉ » NE VEUT PAS DIRE « INDÉPENDANT DE L'ÉVÉNEMENT ». Ce sous-parcours a ses
 * propres interrupteurs — c'est tout son intérêt, on peut demander moins à un
 * accompagnant qu'au porteur. Mais il ne peut pas demander PLUS que ce que l'événement
 * propose : une billetterie fermée au parcours principal n'a rien à vendre à personne.
 *
 * ⚠️ C'ÉTAIT LE CAS PAR DÉFAUT, PAS UN CAS LIMITE. Les deux défauts étaient exactement
 * OPPOSÉS : l'étape billetterie du porteur est `optIn` (éteinte tant qu'on ne la demande
 * pas), celle de l'accompagnant `onByDefault` (allumée tant qu'on ne l'éteint pas). Sur
 * un événement neuf, on ne vendait donc rien au porteur et tout à ses accompagnants — et
 * comme leurs articles tombent SUR LA FACTURE DU PORTEUR, on lui facturait ce qu'on ne
 * lui avait jamais proposé.
 *
 * ⚠️ LES JOURS NE SONT PAS DANS CETTE TABLE, et c'est délibéré : ce n'est pas un module,
 * c'est une propriété de l'événement. Un événement a ses jours que l'on demande au
 * porteur de choisir ou non ; les demander à l'accompagnant reste un choix qui se tient.
 */
const BEARER_MODULE: Record<string, string> = {
  articles: "/registration/articles",
  conferences: "/registration/conferences",
};

/**
 * Le module est-il ouvert sur le parcours principal ?
 *
 * ⚠️ LA CLÉ EST ENGENDRÉE, JAMAIS ÉCRITE ICI — `stepShowKey` sur le registre, exactement
 * comme le fil principal la lit. Recopiée à la main, elle aurait pu diverger d'un
 * renommage, et le sous-parcours aurait alors consulté un interrupteur que plus personne
 * ne pose.
 */
function moduleOpen(read: Read, key: string): boolean {
  const path = BEARER_MODULE[key];
  if (!path) return true;
  const screen = SCREENS.find((s) => s.path === path);
  return !screen || on(read, stepShowKey(screen));
}

/**
 * CE QU'UN ACCOMPAGNANT TRAVERSE RÉELLEMENT.
 *
 * ⚠️ LES MÊMES DEUX ENTRÉES QUE LE FIL PRINCIPAL : les interrupteurs de l'organisateur,
 * et ce que l'ÉVÉNEMENT porte. Un salon d'un seul jour n'a pas de dates à faire choisir
 * — pour l'accompagnant pas plus que pour le porteur — et un événement sans article n'a
 * pas de billetterie à servir.
 *
 * ⚠️ ET LES CONFÉRENCES NE SE JUGENT PAS D'ICI. Savoir s'il y en a demande une requête
 * autorisée par une session, que ce calcul n'a pas : l'étape ne dépend donc que de son
 * interrupteur, comme dans le fil principal.
 */
export function companionSteps(
  read: Read,
  {
    days,
    articles,
    companyChoice,
  }: {
    days?: ReadonlyArray<unknown>;
    articles?: boolean;
    /** A coworker to place among several companies of the bearer: see `COMPANY_STEP`. */
    companyChoice?: boolean;
  } = {},
): ReadonlyArray<CompanionStep> {
  const steps = COMPANION_FUNNEL.filter((step) => {
    // Le module d'abord : son interrupteur à lui ne peut pas rouvrir ce que l'événement
    // a fermé. Voir `MODULE_DU_PORTEUR`.
    if (!moduleOpen(read, step.key)) return false;
    if (step.removable && !on(read, companionStepKey(step))) return false;
    // Moins de deux jours : rien à choisir. La règle de `funnel/dates.ts`, et elle vaut
    // ici aussi — servie, l'étape poserait une question à une seule réponse possible.
    if (step.key === "dates" && days && days.length <= 1) return false;
    // Un événement qui ne vend rien n'a pas de billetterie à faire traverser.
    if (step.key === "articles" && articles === false) return false;
    return true;
  });
  return companyChoice ? [COMPANY_STEP, ...steps] : steps;
}
